import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  createStandaloneUnitAdmin,
  clearSession,
  fetchCurrentSession,
  installAuthFetchInterceptor,
  loadStoredSession,
  loginWithPassword,
  logoutProductSession,
  saveSession,
  type AuthSession,
  type AuthUser
} from '../services/auth';

type AuthContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  user: AuthUser | null;
  permissions: Set<string>;
  knowledgeGraphAccess: AuthSession['knowledgeGraphAccess'] | null;
  customerPortalAccess: AuthSession['customerPortalAccess'] | null;
  login: (input: { username: string; password: string; organizationSlug: string }) => Promise<void>;
  bootstrap: (input: {
    organizationName: string;
    organizationSlug: string;
    username: string;
    password: string;
    fullName?: string;
    email?: string;
    licenseActivationKey: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function toPermissionSet(session: AuthSession | null): Set<string> {
  return new Set((session?.permissions || []).filter(Boolean));
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    installAuthFetchInterceptor();
    const handleSessionExpired = () => {
      setSession(null);
      setIsLoading(false);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
    const stored = loadStoredSession();
    if (!stored) {
      setSession(null);
      setIsLoading(false);
      return () => {
        window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
      };
    }
    saveSession(stored);
    void fetchCurrentSession(stored.token)
      .then(current => {
        const hydrated: AuthSession = {
          token: stored.token,
          tokenType: stored.tokenType || 'Bearer',
          user: current.user,
          permissions: Array.isArray(current.permissions) ? current.permissions : [],
          knowledgeGraphAccess: current.knowledgeGraphAccess,
          customerPortalAccess: current.customerPortalAccess
        };
        saveSession(hydrated);
        setSession(hydrated);
      })
      .catch(() => {
        clearSession();
        setSession(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired as EventListener);
    };
  }, []);

  const login = useCallback(async (input: { username: string; password: string; organizationSlug: string }) => {
    const next = await loginWithPassword(input);
    saveSession(next);
    setSession(next);
  }, []);

  const bootstrap = useCallback(async (input: {
    organizationName: string;
    organizationSlug: string;
    username: string;
    password: string;
    fullName?: string;
    email?: string;
    licenseActivationKey: string;
  }) => {
    const next = await createStandaloneUnitAdmin(input);
    saveSession(next);
    setSession(next);
  }, []);

  const logout = useCallback(async () => {
    const token = session?.token || null;
    try {
      if (token) await logoutProductSession(token);
    } catch (error) {
      console.warn('[auth] server sign-out could not be completed', error);
    } finally {
      clearSession();
      setSession(null);
    }
  }, [session?.token]);

  const permissions = useMemo(() => toPermissionSet(session), [session]);
  const hasPermission = useCallback(
    (permissionKey: string) => permissions.has(permissionKey),
    [permissions]
  );
  const hasAnyPermission = useCallback(
    (permissionKeys: string[]) => permissionKeys.some(key => permissions.has(key)),
    [permissions]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated: Boolean(session?.token),
      token: session?.token || null,
      user: session?.user || null,
      permissions,
      knowledgeGraphAccess: session?.knowledgeGraphAccess || null,
      customerPortalAccess: session?.customerPortalAccess || null,
      login,
      bootstrap,
      logout,
      hasPermission,
      hasAnyPermission
    }),
    [bootstrap, hasAnyPermission, hasPermission, isLoading, login, logout, permissions, session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return value;
}
